import Toast from 'react-native-toast-message'
import { getCurrentOrgID, getOrganizationEntitlements, getCachedOrgID } from '../data/organization';
import ErrorHandler from '../utils/ErrorHandler';
import { sendBreezeNewPerson } from '../utils/breezeSync';
import { supabase, queryClient } from '../data/client';
import { ensureFreshSession } from '../utils/authRefresh';
import findAssociativeChanges from '../utils/findAssociativeChanges';
import { insertActivity } from './activity';
import { buildUpdateDetails, buildCustomFieldDetails, joinDetails } from '../utils/buildActivityDetails';
import { PEOPLE_SELECT } from '../utils/peopleSelect';
import { saveFile } from '../utils/FileManager';
import * as Crypto from 'expo-crypto';
import useAuthStore from '@/data/store/useAuthStore';
import DataExporter from '@/utils/DataExporter';
import { calculateAge } from '@/utils/filters/advancedFilterUtils';
import useLoader from '@/utils/hooks/useLoader';
import { router } from 'expo-router';
import { DateTime } from 'luxon';
import { personImportFieldGroups } from '@/utils/constants/importFields';
import { getLockedPermissions } from './roles';
import { isTransientError } from '@/utils/requestQueue';
import { signOut } from '@/utils/signOut';
import * as Sentry from '@sentry/react-native';
import { freePermissionFromActions, getLatestEntitlements, isFreeOrg, sanitizeFiltersForPlan, setLatestEntitlements, FREE_SORT_DENIED_KEYS } from '@/utils/organizationEntitlements';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { recordPeopleImportSetup } from '@/data/organizationSetup';

// ── Custom field plan boundary ──────────────────────────────────────────────
// People.customFieldValues is a paid-only column: the plan migration replaces
// the table grant with an explicit column list, so select(*) omits it and
// select(customFieldValues) errors. Paid orgs read/write it through the
// SECURITY DEFINER RPCs below; Free orgs never touch it. Until the migration
// deploys, the RPCs don't exist — callers fall back to the legacy direct
// column behavior so nothing regresses.

/** True unless the entitlements payload positively identifies a Free org. */
const customFieldsEnabled = () => !isFreeOrg(getLatestEntitlements());

const isMissingRpcError = (error) => error?.code === 'PGRST202' || error?.code === '42883' ||
    /could not find the function|does not exist|schema cache/i.test(error?.message || '');

/**
 * Paid-path read of a person's customFieldValues via the sanctioned RPC.
 * Returns the values map, or null when denied/absent/RPC missing.
 */
const getPersonCustomFieldValues = async (personID) => {
    if (!customFieldsEnabled()) return null;
    const { data, error } = await supabase.rpc('get_person_custom_fields', { p_person_id: personID });
    if (error) {
        // 42501: request hit the RPC as anon (signed-out/expired session) and
        // EXECUTE is authenticated-only — expected denial, not a bug.
        if (!isMissingRpcError(error) && error.code !== '42501') {
            ErrorHandler(Error(error.message), { query: 'getPersonCustomFieldValues', personID, error });
        }
        return null;
    }
    return data || null;
};

/**
 * Probes whether the custom-field write RPC exists (pre-migration schemas
 * return a function-not-found error). Lets bulk flows pick one strategy up
 * front instead of failing per row.
 */
const customFieldsRpcAvailable = async () => {
    const { error } = await supabase.rpc('update_person_custom_fields', {
        p_person_id: '00000000-0000-0000-0000-000000000000',
        p_patch: {},
    });
    return !(error && isMissingRpcError(error));
};

/**
 * Merge a customFieldValues patch through the RPC. Falls back to a direct
 * column update only when the RPC is missing (pre-migration schema, where the
 * column is still a plain People column). Returns the merged map on success,
 * true when the RPC returned nothing, false on failure.
 */
const updatePersonCustomFieldValues = async (personID, patch) => {
    if (!patch || Object.keys(patch).length === 0) return true;
    // Defense-in-depth: a positively identified Free org never touches the
    // paid-only column, even if a caller forgot the gate.
    if (!customFieldsEnabled()) return false;
    const { data, error } = await supabase.rpc('update_person_custom_fields', { p_person_id: personID, p_patch: patch });
    if (!error) return data ?? true;
    if (isMissingRpcError(error)) {
        const { error: legacyError } = await supabase.from("People").update({ customFieldValues: patch }).eq("id", personID);
        if (!legacyError) return patch;
        ErrorHandler(Error(legacyError.message), { mutation: 'updatePersonCustomFieldValues-legacy', personID, error: legacyError });
        return false;
    }
    ErrorHandler(Error(error.message), { mutation: 'updatePersonCustomFieldValues', personID, error });
    return false;
};

/**
 * Remove the paid-only column from a People row object on Free orgs. Needed
 * pre-migration where select(*)/select() still returns it inline; post-
 * migration the column is already off the wire (no-op).
 */
const stripCustomFieldsForFree = (row) => {
    if (row && !customFieldsEnabled()) delete row.customFieldValues;
    return row;
};

/**
 * Retrieves information about the logged-in user.
 * @returns {Promise<import('../types/Person').Person|boolean>} - A promise that resolves to an object containing the user's profile information and admin status, or false if an error occurred.
 */
const getLoggedInUserInfo = async () => {
    const emulatedUser = queryClient.getQueryData(['emulatingUser'])

    if(emulatedUser){
        const profile = await getPersonProfile(emulatedUser.id, false);
        if(!profile){
            Toast.show({type: 'error', text2: 'Failed to load emulated user profile'});
            return false;
        }

        return {...profile, isAdmin: false, isAccountOwner: false}
    }

    const fetchAuthUser = async () => {
        const {data} = await supabase.auth.getUser();
        if(!data?.user?.id) return {authUser: null, hasSession: false};

        // Prefer the session's JWT claim over getUser()'s live app_metadata:
        // REST/RLS authorization evaluates the JWT, and another device/process
        // can stamp raw_app_meta_data ahead of this token (org switch), so
        // the claim — not the server value — reflects what queries can see.
        const orgID = (await getCurrentOrgID()) || data.user.app_metadata?.organizationID;
        if(!orgID) return {authUser: null, hasSession: true, noOrg: true};

        const {data: authUser, error} = await supabase.from("RegisteredUsersOrganizations").select("*, RegisteredUser:RegisteredUsers(*)").eq("userID", data.user.id).eq('organizationID', orgID).maybeSingle();

        if(error){
            ErrorHandler(Error(error.message), {query: "getLoggedInUserInfo", authUser, orgID, error}, `Something went wrong fetching the logged in user`)
            return {authUser: null, hasSession: true, membershipError: error};
        }

        return {authUser, hasSession: true};
    };

    let {authUser, hasSession, noOrg, membershipError} = await fetchAuthUser();

    // No org selected (e.g. user on select-organization screen) — authenticated
    // but no org context. This is a valid state, NOT a session expiry. Bail
    // without signing out.
    if(noOrg) return false;

    // A null authUser can be a transient RLS miss from a stale access token
    // (the query returns no row before autoRefresh has minted a fresh JWT).
    // Force a session refresh via the singleton guard (prevents concurrent
    // refresh races) and retry the DB query once. The old 1.5s delay was
    // unreliable on web — browser tab throttling can prevent autoRefresh from
    // firing for several seconds after the tab becomes visible.
    if(!authUser && hasSession){
        try { await ensureFreshSession(); } catch { /* session may already be gone */ }
        ({authUser, hasSession, noOrg, membershipError} = await fetchAuthUser());
    }

    // No session at all (e.g. user on login page, theme-restore effect fires
    // getLoggedInUserInfo) — just bail. Do NOT call signOut() — there's nothing
    // to revoke, and calling it with no session triggers Supabase's lock error.
    if(!authUser){
        if(!hasSession || noOrg) return false;
        // The membership query ERRORED (timeout, abort, 5xx) — that's a
        // transient failure, not proof the membership is gone. Signing out
        // here would kick users to login on a network blip; bail like the
        // other transient paths and let the next fetch retry.
        if(membershipError){
            Sentry.logger.warn('Membership query errored — not signing out', { error: membershipError?.message });
            return false;
        }
        await signOut('local', 'logged-in-user-membership-missing');
        Toast.show({type: 'error', text2: "Your session has expired. Please log in again."})
        router.push("/auth/login");
        return false;
    }

    // Resolve plan BEFORE the heavy profile chain. Free orgs must never run
    // getPersonProfile — it unconditionally fetches attendance, dynamic roles
    // and paid-module embeds (the plan RPC is the only allowed bootstrap).
    // A failed entitlement call is NOT Free: fall through to the paid path
    // and keep the existing transient-failure safeguards.
    let entitlements = null;
    try {
        entitlements = await getOrganizationEntitlements();
        if (entitlements?.member === true) {
            setLatestEntitlements(entitlements);
            // Prime the shared hook cache so layouts don't refetch the RPC.
            queryClient.setQueryData(QueryKeys.organizationEntitlements.current(getCachedOrgID()), entitlements);
        }
    } catch { /* transient — paid path below carries its own fallbacks */ }

    const freePlan = entitlements?.member === true && entitlements?.softwarePlan === 'free';
    const fetchProfile = () => freePlan
        ? getFreePersonProfile(authUser.personID, entitlements)
        : getPersonProfile(authUser.personID, authUser.isAdmin);

    let profile = await fetchProfile();

    // Same stale-token guard: getPersonProfile can return an empty/stub result
    // ({}, false, or a {canAny/canAll/can} stub with no `id`) when the People
    // query hits a stale token or a transient network blip. Retry once with a
    // fresh session — a real "person deleted" case fails both times and still
    // signs out. Use `!profile?.id` (instead of the old
    // `Object.keys(profile).length === 0`) so the stub case is also caught.
    if(!profile || !profile?.id){
        try { await ensureFreshSession(); } catch { /* session may already be gone */ }
        profile = await fetchProfile();
    }

    // Entitlements RPC failed AND the heavy profile failed: this may be a Free
    // org whose paid embeds are now denied by RLS — try the Free-safe fetch
    // before concluding the person row is gone.
    if((!profile || !profile?.id) && !freePlan && entitlements == null){
        try {
            profile = await getFreePersonProfile(authUser.personID, {
                member: true,
                softwarePlan: 'free',
                actions: null,
                modules: null,
                isAdmin: authUser.isAdmin,
                isAccountOwner: authUser.isAccountOwner || false,
            });
        } catch { /* leave profile falsy — existence check below decides */ }
    }

    if(!profile || !profile?.id){
        // The complex getPersonProfile query (many joins) can fail on
        // transient network errors even when the person row exists. Before
        // signing out, do a minimal existence check — if the row is there,
        // the profile query was a transient failure, not a real deletion.
        // Don't sign out on transient failures; just bail and let the caller
        // retry later.
        try {
            const {data: exists, error: existsError} = await supabase
                .from("People")
                .select("id")
                .eq("id", authUser.personID)
                .maybeSingle();

            if(existsError && isTransientError(existsError)){
                Sentry.logger.warn('Transient error during profile existence check — not signing out', {personID: authUser.personID, error: existsError?.message});
                return false;
            }

            if(exists?.id){
                Sentry.logger.warn('Profile query failed but person row exists — transient failure, not signing out', {personID: authUser.personID});
                return false;
            }
        } catch(e) {
            // Network error during existence check — can't confirm deletion,
            // don't sign out
            Sentry.logger.warn('Existence check failed during profile lookup — not signing out', {personID: authUser.personID, error: e?.message});
            return false;
        }

        await signOut('local', 'logged-in-user-profile-missing');
        Toast.show({type: 'error', text2: "Your session has expired. Please log in again."})
        router.push("/auth/login");
        return false;
    }

    const userSettings = {
        notificationSettings: authUser.RegisteredUser?.notificationSettings,
        theme: authUser.RegisteredUser?.theme.toLowerCase(),
        autoMilestoneCelebrationsEnabled: authUser.RegisteredUser?.autoMilestoneCelebrationsEnabled !== false,
    }

    if(userSettings.theme !== 'default'){
        useAuthStore.setState({theme: userSettings.theme})
    }

    // For Free profiles, isAdmin comes from the entitlements RPC (authoritative
    // for the fixed action matrix); Paid profiles carry no isAdmin and keep
    // the membership-row flag.
    return {...profile, isAdmin: profile?.isAdmin ?? authUser.isAdmin, isAccountOwner: profile?.isAccountOwner ?? authUser.isAccountOwner ?? false, accountEmail: authUser.RegisteredUser?.email, accountID: authUser.userID ?? authUser.RegisteredUser?.id, userSettings}
}

/**
 * Retrieves the profile information of a person from the database.
 * @param {string} id - The ID of the person.
 * @param {boolean} [isAdmin=false] - Indicates whether the person is an admin.
 * @returns {Promise<import('../types/Person').Person>} - A promise that resolves to the profile information of the person.
 */
const getPersonProfile = async (id, isAdmin = false) => {
    if(!id){
        return false;
    }
    // Reject non-uuid values (e.g. the string "undefined" from a bad route param)
    // before hitting Postgres — otherwise it raises
    // `invalid input syntax for type uuid: "undefined"`. See CHURCH-APP-12M.
    if(typeof id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)){
        return false;
    }

    // Plan-aware profile: a positively-Free org takes the trimmed profile —
    // the paid embeds below are denied by the plan gates and would only fail
    // noisily. Unknown/missing entitlements keep the existing paid path.
    let entitlements = getLatestEntitlements();
    if (entitlements == null) {
        try {
            entitlements = await getOrganizationEntitlements();
            if (entitlements?.member === true) setLatestEntitlements(entitlements);
        } catch { /* unknown → paid path below */ }
    }
    if (isFreeOrg(entitlements)) {
        return getFreePersonProfile(id, entitlements);
    }

    return supabase
    .from("People")
    .select(`${PEOPLE_SELECT},
        Notifications(*, NotificationType:NotificationTypes(id, name, module, location)),
        Shortcuts(*),
        Address:Addresses(*, HouseholdMembers:People(id,firstName,lastName,breezePersonID,avatarUpdatedAt, avatarStorageKey, familyRole)),
        SmallGroupsAsMember:SmallGroupMembers!fk_SmallGroupMembers_memberID(*, SmallGroup:SmallGroups(*, SmallGroupType:SmallGroupTypes(id, name, icon), Students:SmallGroupMembers(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), Event:Events(EventInstances(startTime, notes)))),
        SmallGroupsAsLeader:SmallGroups!SmallGroups_leaderID_fkey(*, SmallGroupType:SmallGroupTypes(id, name, icon), Students:SmallGroupMembers(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), Event:Events(EventInstances(startTime, notes))),
        TaskInstances!TaskInstancePeople(*, EventInstance:EventInstances(*, Event:Events(name), TaskInstances(*, Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)))), Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)), Task:Tasks(*), VolunteerTeam:VolunteerTeams(id, name, VolunteerTeamTasks(taskID, assigneesNeeded))),
        Roles(id, name, description, Permissions(*)),
        ManagingRoles:RolePeople(Role:Roles(*), canManage),
        BlackoutPeriods(*),
        RegisteredUsersOrganizations(personID, organizationID, isAdmin),
        Family:Families(*, FamilyMembers:People!inner(id, firstName, lastName, gender, addressID, familyRole, breezePersonID, avatarUpdatedAt, avatarStorageKey, isArchived)),
        InvitedBy:invitedByID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        Phones:PeoplePhones(phoneNumber, label, smsOptInStatus, private)`)
    .eq("id", id)
    .eq("TaskInstances.isDeleted", false)
    .eq("TaskInstances.completed", false)
    .eq("ManagingRoles.canManage", true)
    .gt("BlackoutPeriods.endDate", new Date().toISOString())
    .eq("RegisteredUsersOrganizations.personID", id)
    .eq("Families.People.isArchived", false)
    .order("dueDate", {referencedTable: "TaskInstances", ascending: true})
    .order("sentAt", {referencedTable: "Notifications", ascending: false})
    .order("startTime", {referencedTable: "SmallGroupsAsLeader.Event.EventInstances", ascending: false})
    .maybeSingle()
    .then(async ({data: person, error}) => {
        if(error){
            if(!isTransientError(error) && !error.message.includes('Could not find a relationship between')){
                ErrorHandler(Error(error.message), {personID: id, ...error}, `Something went wrong fetching that persons data`)
            }
            return {canAny: () => false, canAll: () => false, can: () => false};
        }
        else if(!person){
            // Missing person is an expected state (deleted person, bad route param) —
            // return null without reporting to Sentry. Real query errors handled above.
            return null;
        }

        // customFieldValues has no column grant — PEOPLE_SELECT never
        // includes it, so compose it via the paid RPC so consumers keep
        // the same shape.
        const needsCustomFieldValues = customFieldsEnabled() && !('customFieldValues' in person);
        const [{data: attendance}, dynamicRoles, rpcCustomFieldValues] = await Promise.all([
            supabase.rpc('get_person_attendance', {personid: id}),
            getPersonsDynamicRoles(person.id),
            needsCustomFieldValues ? getPersonCustomFieldValues(person.id) : Promise.resolve(null),
        ]);
        if (rpcCustomFieldValues != null) person.customFieldValues = rpcCustomFieldValues;

        person.Roles = [...person.Roles, ...dynamicRoles]
        person.mostRecentVisit = attendance && attendance.length > 0 ? new Date(attendance[0].mostRecentVisit) : null
        person.firstVisit = attendance && attendance.length > 0 ? new Date(attendance[0].firstVisit) : null
        person.timesAttended = attendance && attendance.length > 0 ? attendance[0].timesAttended ?? 0 : 0

        person.TaskInstances = person.TaskInstances?.filter(i => i.completed === false).map(t => ({...t, Assignees: t.Assignees?.map(a => ({...a, ...a.Person}))}))

        person.isAdmin = isAdmin
        await assignPermissions(person);

        person.ManagingRoles = person.ManagingRoles?.map(role => role.Role);
        person.SmallGroupsAsMember = person.SmallGroupsAsMember?.map(b => b.SmallGroup).filter(Boolean);
        person.SmallGroupsAsLeader = person.SmallGroupsAsLeader?.sort((a, b) => a.Event?.EventInstances?.[0]?.startTime > b.Event?.EventInstances?.[0]?.startTime ? -1 : 1);

        return {...person, isAdmin: isAdmin}
    })
    .catch((e) => {
        ErrorHandler(e, {query: "getPerson", input: {id: id}}, `Something went wrong fetching that persons data`)
        return {};
    })
}

/**
 * Retrieves a person for merging with minimal data.
 * @param {string} id - The ID of the person to fetch for merge.
 * @returns {Promise<object>} The person object for merge.
 */
// Lightweight person fetch for contexts that only need identity + contact info
// (e.g., the registrant detail modal in the scan flow). Avoids the heavy
// getPersonProfile query (TaskInstances, Notifications, SmallGroups, ...).
const getPersonBasic = async (id) => {
    if (!id) return null;

    const { data, error } = await supabase
        .from('People')
        .select('id, firstName, lastName, email, dateOfBirth, avatarUpdatedAt, avatarStorageKey, Address:Addresses(*), Phones:PeoplePhones(phoneNumber, label, smsOptInStatus, private)')
        .eq('id', id)
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: 'getPersonBasic', personID: id, error }, 'Something went wrong fetching that person');
        return null;
    }

    return data;
}

/**
 * Free-plan person profile via the get_free_person_profile SECURITY DEFINER
 * RPC — explicit permitted fields only, never customFieldValues, roles,
 * attendance, files/tasks or admin internals. While the RPC is undeployed
 * (function-not-found) we fall back to an equivalent direct select; that path
 * keeps explicit permitted columns only — never `*` (keeps
 * customFieldValues off the wire) and never paid embeds (Roles, tasks,
 * attendance, small groups, training, files, notifications). Attaches the
 * fixed Free action map so assignPermissions resolves plan-aware can()
 * without any dynamic-role queries.
 * @param {string} id - person id
 * @param {object} entitlements - hydrated get_organization_entitlements payload
 */
const getFreePersonProfile = async (id, entitlements) => {
    if (!id) return null;

    let person = null;
    const {data: rpcProfile, error: rpcError} = await supabase.rpc('get_free_person_profile', { p_person_id: id });
    if (!rpcError) {
        // Deployed + authorized: jsonb profile. Deployed + denied/missing
        // person: NULL — treat as not found, don't probe further.
        if (!rpcProfile) return null;
        person = rpcProfile;
    } else {
        if (!isMissingRpcError(rpcError)) {
            ErrorHandler(Error(rpcError.message), {query: "getFreePersonProfile", personID: id, error: rpcError});
        }
        // RPC missing (pre-migration) or failed — the explicit select below is
        // column-safe on both schemas.
        const {data: selected, error} = await supabase
            .from("People")
            .select(`id, organizationID, firstName, lastName, fullName, gender, dateOfBirth, email, phone,
                isArchived, breezePersonID, addressID, familyID, familyRole, invitedByID, invitedByNote,
                avatarUpdatedAt, avatarStorageKey, barcode,
                Address:Addresses(*, HouseholdMembers:People(id,firstName,lastName,breezePersonID,avatarUpdatedAt, avatarStorageKey, familyRole)),
                Family:Families(*, FamilyMembers:People!inner(id, firstName, lastName, gender, addressID, familyRole, breezePersonID, avatarUpdatedAt, avatarStorageKey, isArchived)),
                Phones:PeoplePhones(phoneNumber, label, smsOptInStatus, private),
                RegisteredUsersOrganizations(personID, organizationID, isAdmin)`)
            .eq("id", id)
            .eq("Families.People.isArchived", false)
            .maybeSingle();

        if(error){
            ErrorHandler(Error(error.message), {query: "getFreePersonProfile", personID: id, error}, `Something went wrong fetching that persons data`);
            return null;
        }
        person = selected;
    }
    if(!person) return null;

    // Fixed Free matrix comes from the entitlements RPC — the actions map
    // already reflects admin vs member, so no role/lock lookups run here.
    person.freeActions = entitlements?.actions || null;
    person.freeModules = entitlements?.modules || null;
    person.isAdmin = (entitlements?.isAdmin === true) || (entitlements?.isAccountOwner === true);
    await assignPermissions(person);

    return person;
}

const getPersonForMerge = async (id) => {
    // Paid embeds (attendance, tasks, small groups, conversations, roles,
    // blackout periods) are denied by table grants on Free — a positively
    // identified Free org must not even ask for them or the whole select
    // fails. customFieldValues is excluded from PEOPLE_SELECT either way.
    const free = !customFieldsEnabled();
    const paidEmbeds = free ? '' : `
        Attendance!fk_Attendance_Person(count),
        SmallGroupsAsLeader:SmallGroups!SmallGroups_leaderID_fkey(count),
        TaskInstancePeople(count),
        PersonConversations(count),
        RolePeople(count),
        BlackoutPeriods(count),`;
    const {data: person, error} = await supabase.from("People")
    .select(`${PEOPLE_SELECT},${paidEmbeds}
        Notifications(count),
        Address:Addresses(*, HouseholdMembers:People(id,firstName,lastName,breezePersonID,avatarUpdatedAt, avatarStorageKey, familyRole)),
        RegisteredUsersOrganizations(personID, organizationID, isAdmin),
        InvitedBy:People!invitedByID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)`)
    .eq("id", id)
    .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {personID: id, ...error}, `Something went wrong fetching that person for a merge`)
        return {};
    }

    // customFieldValues has no column grant — paid orgs compose it via the
    // RPC; Free orgs merge scalar fields only (values stay untouched).
    if (person && !customFieldsEnabled()) {
        delete person.customFieldValues;
    } else if (person && !('customFieldValues' in person)) {
        const cfv = await getPersonCustomFieldValues(person.id);
        if (cfv) person.customFieldValues = cfv;
    }

    return person
}

/**
 * Get dynamic roles for a person based on filters.
 * @param {string} personID - The ID of the person.
 * @returns {Promise<object[]>} List of dynamic roles.
 */
const getPersonsDynamicRoles = async (personID) => {
    if(!personID){
        return [];
    }

    const orgID = await getCurrentOrgID();
    if (!orgID) return [];

    const {data, error} = await supabase.rpc('check_person_in_filters', {personid: personID, organizationid: orgID});

    if(error){
        if(error.code !== 'PGRST002'){
            ErrorHandler(Error(error.message), {query: "getPersonsDynamicRoles", personID, orgID, error})
        }
        return [];
    }

    return data.map(d => ({
        id: d.group_id,
        name: d.group_name,
        description: d.group_description,
        Permissions: d.permissions || [],
        quickFilterID: d.filter_id,
        quickFilterName: d.filter_name,
        isDynamicRole: true
    }))
}

/**
 * Assigns permission functions to a person object.
 * Locked permissions (per-org via OrganizationLockedPermissions) bypass the
 * admin shortcut — only the account owner or an explicit grant satisfies them.
 * @param {import('../types/Person').Person} person - The person object to augment with permissions.
 * @returns {Promise<void>}
 */
const assignPermissions = async (person) => {
    if(!person) return;

    // Free plan: resolve the fixed action matrix — never query roles/locks.
    // freeActions serializes with the profile, so hydrated/persisted users
    // rebuild the same plan-aware functions without a network call.
    if('freeActions' in person){
        const actions = person.freeActions || {};
        person.allPermissions = person.allPermissions || [];
        person.lockedPermissions = Array.isArray(person.lockedPermissions) ? person.lockedPermissions : [];
        person.can = (permission) => freePermissionFromActions(actions, permission);
        person.canAll = (permissions) => (permissions || []).every(p => freePermissionFromActions(actions, p));
        person.canAny = (permissions) => (permissions || []).some(p => freePermissionFromActions(actions, p));
        return;
    }

    const permissions = new Set();
    person.Roles?.forEach(role => {
        role.Permissions?.forEach(permission => {
            permissions.add(permission.name);
        })
    })
    person.allPermissions = [...permissions];

    // Locked permission names must be resolved from the Permissions table
    // independently of the user's own roles. The previous implementation only
    // flagged a permission as locked when the user's roles already granted it,
    // so an admin with no role permissions had an empty `lockedPermissions`
    // set and bypassed the lock via the admin shortcut.
    if(!Array.isArray(person.lockedPermissions)){
        const orgID = await getCurrentOrgID();
        const lockedPermissions = await getLockedPermissions(orgID);
        person.lockedPermissions = [...lockedPermissions.names];
    }

    function can(permission) {
        if(this.lockedPermissions.includes(permission)) {
            return this.isAccountOwner || this.allPermissions.includes(permission);
        }
        return this.isAdmin || this.allPermissions.includes(permission);
    }

    function canAll(permissions) {
        return permissions.every(p => {
            if(this.lockedPermissions.includes(p)) {
                return this.isAccountOwner || this.allPermissions.includes(p);
            }
            return this.isAdmin || this.allPermissions.includes(p);
        })
    }

    function canAny(permissions) {
        return permissions.some(p => {
            if(this.lockedPermissions.includes(p)) {
                return this.isAccountOwner || this.allPermissions.includes(p);
            }
            return this.isAdmin || this.allPermissions.includes(p);
        })
    }

    person.can = can
    person.canAll = canAll
    person.canAny = canAny
}

/**
 * Updates the avatarUpdatedAt field for a person.
 * @param {string} personID - The ID of the person.
 * @returns {Promise<object>} The updated person data or error.
 */
const updateAvatarUpdatedAt = async ({personID, storageKey}) => {
    const {error, data} = await supabase.from("People").update({avatarUpdatedAt: new Date(), avatarStorageKey: storageKey}).eq("id", personID).select("id, avatarUpdatedAt").maybeSingle()

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateAvatarUpdatedAt", personID})
    };

    return data
}

/**
 * Updates barcodes for a list of people.
 * @param {object} params - The parameters for the function.  
 * @param {string[]} params.personIDs - Array of person IDs.
 * @param {string} params.barcode - The barcode value.
 * @returns {Promise<void>}
 */
const updateBarcodes = async ({personIDs, barcode}) => {
    const orgID = await getCurrentOrgID();

    const {error} = await supabase.from("People").update({barcode}).in("id", personIDs).eq("organizationID", orgID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateBarcodes", personIDs, barcode})
    };
}

/**
 * Changes the admin status for a person in the organization.
 * @param {object} params - The parameters for the function.
 * @param {string} params.personID - The ID of the person.
 * @param {boolean} params.isAdmin - Admin status to set.
 * @returns {Promise<void>}
 */
const changeAdminStatus = async ({personID, isAdmin}) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from("RegisteredUsersOrganizations").update({isAdmin: isAdmin}).eq("personID", personID).eq("organizationID", orgID).select('Person:People(firstName, lastName)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "changeAdminStatus", personID, isAdmin, error}, `Something went wrong changing that persons admin status`)
    }
    else{
        const name = [data?.Person?.firstName, data?.Person?.lastName].filter(Boolean).join(' ') || 'User';
        Toast.show({type:'success', text2: isAdmin ? `${name} is now an admin for your organization.` : `${name} is no longer an admin for your organization.`});
    }
}

/**
 * Gets a paginated list of people with optional filters.
 * @param {object} params - The parameters for the function.
 * @param {number} [params.pageParam=0] - Pagination page.
 * @param {string} [params.searchTerm] - Search term.
 * @param {object} [params.filters={}] - Filters object.
 * @param {string} [params.filterLogic='AND'] - Filter logic.
 * @returns {Promise<object>} Paginated people data.
 */
const sanitizeFilterConditions = (filters) => {
    // Plan gate first — a Free org must never send paid-only filter shapes
    // (customFieldId, roles, attendance) to filter_people; it rejects with
    // 42501 rather than ignoring them.
    const planSafe = sanitizeFiltersForPlan(filters, getLatestEntitlements());
    if (!planSafe?.conditions) return planSafe;
    filters = planSafe;

    const cleanedConditions = filters.conditions.filter(c => {
        if (['is_empty', 'is_not_empty'].includes(c.operator)) return true;
        if (c.value === '' || c.value === null || c.value === undefined) return false;
        if (c.operator === 'between' && (c.value2 === '' || c.value2 === null || c.value2 === undefined)) return false;
        if (Array.isArray(c.value) && c.value.length === 0) return false;
        return true;
    });

    return { ...filters, conditions: cleanedConditions };
};

const ADDRESS_COMPONENT_SORT_KEYS = new Set(['street', 'streetLine2', 'city', 'state', 'zip']);

const getPeople = async ({pageParam = 0, searchTerm = '', filters = {}, filterLogic = 'AND', sortKey, sortDirection, signal}) => {
    const orgID = await getCurrentOrgID();
    const searchValue = searchTerm?.trim() || undefined;
    const sanitizedFilters = sanitizeFilterConditions(filters);
    // Paid-only sort keys (attendance, custom_*) are rejected server-side on
    // Free — drop them before the RPC.
    const effectiveSortKey = isFreeOrg(getLatestEntitlements()) && sortKey && (FREE_SORT_DENIED_KEYS.includes(sortKey) || sortKey.startsWith('custom:')) ? null : sortKey;
    const rpcName = ADDRESS_COMPONENT_SORT_KEYS.has(effectiveSortKey) ? 'filter_people_address_sort' : 'filter_people';

    // `signal` comes from React Query — superseded search/filter queries abort
    // their in-flight request instead of occupying a request-queue slot until
    // the 60s timeout. See CHURCH-APP-14J.
    const {data: people, error} = await supabase.rpc(rpcName, {filters: {...sanitizedFilters, search: searchValue, sortKey: effectiveSortKey, sortDirection}, organizationid: orgID, logictype: filterLogic, record_limit: 50, record_offset: (pageParam || 0) * 50}).abortSignal(signal);

    if(error){
        // Aborted superseded fetch (search/filter key changed mid-flight and
        // React Query cancelled the consumed signal) — not a real failure.
        if (signal?.aborted) return {nextPage: undefined, data: []};
        ErrorHandler(Error(error.message), {query: "getPeople", params: {filters: {...filters, search: searchValue}, organizationid: orgID, logictype: filterLogic, record_limit: 50, record_offset: (pageParam || 0) * 50}}, `Something went wrong fetching people`)
        return {nextPage: undefined, data: []}
    }

    const allData = (!people || !people[0].people?.results) ? [] : people[0].people?.results?.map(p => ({...p, HouseholdMembers: p.Address?.People?.filter(a => a.id != p.id).map(a => a)}))
    return {nextPage: allData.length < 50 ? undefined : pageParam + 1, data: allData, count: people[0].people?.totalCount}
}


const getPeopleWithConversationAccess = async (orgID) => {
    const { data, error } = await supabase.rpc('get_people_with_conversation_access', { p_org_id: orgID });
    if (error) {
        ErrorHandler(Error(error.message), { query: 'getPeopleWithConversationAccess', input: { organizationID: orgID } }, 'Something went wrong fetching people with conversation access');
        return [];
    }
    return data || [];
}


const exportPeopleData = async ({totalCount, searchTerm, filters, filterLogic = 'AND', selectedFields = {}, people: providedPeople, attendance = [], filename = "people"} = {}) => {
    useLoader.getState().enable();

    try{
        let allData;

        if (providedPeople) {
            allData = providedPeople;
        } else {
            const orgID = await getCurrentOrgID();
            const sanitizedFilters = sanitizeFilterConditions(filters);
            const {data: people, error} = await supabase.rpc("filter_people", {filters: {...sanitizedFilters, search: searchTerm?.trim() || undefined}, organizationid: orgID, logictype: filterLogic, record_limit: totalCount, record_offset: 0});

            if(error){
                ErrorHandler(Error(error.message), {query: "getPeople", params: {filters: {...filters, search: searchTerm?.trim() || undefined}, organizationid: orgID, logictype: filterLogic, record_limit: totalCount, record_offset: 0}}, `Something went wrong fetching people`)
                return []
            }

            allData = (!people || !people[0].people?.results) ? [] : people[0].people?.results?.map(p => ({...p, HouseholdMembers: p.Address?.People?.filter(a => a.id != p.id).map(a => a)}))
        }
        
        const { basicFields = [], customFields = [], checkinFields: selectedCheckinFields = [] } = selectedFields;
        // Custom fields are paid-only — never export them on a Free plan.
        const exportableCustomFields = customFieldsEnabled() ? customFields : [];

        // Build attendance lookup by attendeeID for check-in detail columns
        const attendanceByPersonID = {};
        if (selectedCheckinFields.length > 0) {
            (attendance || []).forEach(a => {
                attendanceByPersonID[a.attendeeID] = a;
            });
        }

        const exportData = allData.map(person => {
            const rowData = {};

            if (basicFields.includes('id')) rowData.id = person.id;
            if (basicFields.includes('firstName')) rowData.firstName = person.firstName;
            if (basicFields.includes('lastName')) rowData.lastName = person.lastName;
            if (basicFields.includes('dateOfBirth')) rowData.dateOfBirth = person.dateOfBirth;
            if (basicFields.includes('age')) rowData.age = calculateAge(person.dateOfBirth);
            if (basicFields.includes('gender')) rowData.gender = person.gender;
            if (basicFields.includes('email')) rowData.email = person.email;
            if (basicFields.includes('phone')) rowData.phone = person.Phones?.map(p => p.phoneNumber).join(", ") || "";
            const addr = person.Address;
            if (basicFields.includes('address') || basicFields.includes('street')) rowData.street = addr?.street || '';
            if (basicFields.includes('address') || basicFields.includes('streetLine2')) rowData.streetLine2 = addr?.streetLine2 || '';
            if (basicFields.includes('address') || basicFields.includes('city')) rowData.city = addr?.city || '';
            if (basicFields.includes('address') || basicFields.includes('state')) rowData.state = addr?.state || '';
            if (basicFields.includes('address') || basicFields.includes('zip')) rowData.zip = addr?.zip || '';

            exportableCustomFields.forEach(cf => {
                rowData[cf.name] = person.customFieldValues?.[cf.id] || "";
            });

            if (selectedCheckinFields.length > 0) {
                const record = attendanceByPersonID[person.id];
                if (selectedCheckinFields.includes('checkedIn')) rowData.checkedIn = record ? "Yes" : "No";
                if (selectedCheckinFields.includes('checkedInLocation')) rowData.checkedInLocation = record?.Location?.name || "";
                if (selectedCheckinFields.includes('checkedInTime')) rowData.checkedInTime = record?.createdAt ? DateTime.fromISO(record.createdAt).toLocaleString(DateTime.DATETIME_MED) : "";
                if (selectedCheckinFields.includes('checkedOut')) rowData.checkedOut = record?.checkedOutAt ? "Yes" : "No";
                if (selectedCheckinFields.includes('checkedOutTime')) rowData.checkedOutTime = record?.checkedOutAt ? DateTime.fromISO(record.checkedOutAt).toLocaleString(DateTime.DATETIME_MED) : "";
            }

            return rowData;
        });

        DataExporter.exportToCSV(exportData, filename);
    } catch (error) {
        ErrorHandler(Error(error.message), {query: "exportPeopleData", totalCount, searchTerm, filters, filterLogic}, `Something went wrong exporting people`)
    } finally {
        useLoader.getState().disable();
    }
}

/**
 * Retrieves all person custom fields for the current org.
 * @returns {Promise<object[]>} List of person custom fields.
 */
const getPersonCustomFields = async () => {
    const orgID = await getCurrentOrgID(); 
    // No org (logged out) — skip the query. .eq('organizationID', null) errors
    // in Postgres: `invalid input syntax for type uuid: "null"` (22P02).
    // See CHURCH-APP-1EV.
    if (!orgID) return [];

    const {data, error} = await supabase.from("CustomFields").select("id, name, type, options, position, isProtected, required, module, Section:CustomFieldSections(*)").eq("organizationID", orgID).eq("module", "People").eq("isDeleted", false);

    if(error){
        ErrorHandler(Error(error.message), {query: "getPersonCustomFields", orgID, ...error}, `Something went wrong fetching the custom fields`)
        return [];
    }

    return data.sort((f1, f2) =>  f1.Section.position - f2.Section.position || f1.position - f2.position || f1.name.localeCompare(f2.name));
}

const getPersonCustomField = async (id) => {
    const orgID = await getCurrentOrgID(); 

    const {data, error} = await supabase.from("CustomFields").select("id, name, position, type, options, module, Section:CustomFieldSections(*)").eq("organizationID", orgID).eq("module", "People").eq("id", id).eq("isDeleted", false).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getPersonCustomFields", orgID, ...error}, `Something went wrong fetching the custom fields`)
        return [];
    }

    return data
}

/**
 * Retrieves all person custom field sections for the current org.
 * @returns {Promise<object[]>} List of custom field sections.
 */
const getPersonCustomFieldSections = async () => {
    const orgID = await getCurrentOrgID(); 

    const {data, error} = await supabase.from("CustomFieldSections").select("*, CustomFields(*)").eq("organizationID", orgID).eq("type", "People").order('position', {ascending: true}).order('position', {referencedTable: 'CustomFields', ascending: true});

    if(!error && data){
        data.forEach(section => {
            if(section.CustomFields){
                section.CustomFields = section.CustomFields.filter(field => !field.isDeleted);
            }
        });
    }

    if(error){
        ErrorHandler(Error(error.message), {query: "getPersonCustomFieldSections", orgID, ...error}, `Something went wrong fetching the custom field sections`)
        return [];
    }

    return data;
}

const getPersonCustomFieldSection = async (id) => {
    const orgID = await getCurrentOrgID(); 

    const {data, error} = await supabase.from("CustomFieldSections").select("*, Fields:CustomFields(*)").eq("organizationID", orgID).eq("type", "People").eq("id", id).maybeSingle();

    if(!error && data && data.Fields){
        data.Fields = data.Fields.filter(field => !field.isDeleted);
    }

    if(error){
        ErrorHandler(Error(error.message), {query: "getPersonCustomFieldSections", orgID, ...error}, `Something went wrong fetching the custom field sections`)
        return [];
    }

    return data;
}


/**
 * Saves a person custom field (create or update).
 * @param {object} params - The parameters for the function.
 * @param {object} params.details - The field details.
 * @returns {Promise<object>} The created or updated field.
 */
const savePersonCustomField = async ({details}) => {
    const orgID = await getCurrentOrgID(); 

    let field = {
        id: details.id,
        name: details.name,
        type: details.type,
        module: 'People',
        sectionID: details.sectionID,
        options: details.options || [],
        organizationID: orgID
    }

    if(details.position !== undefined) field.position = details.position;
    if(details.required !== undefined) field.required = details.required;
    if(details.isProtected !== undefined) field.isProtected = details.isProtected;

    if(!field.id){
        return createPersonCustomField(field); 
    }
    else{
        return updatePersonCustomField(field);
    }
}

/**
 * Creates a new person custom field.
 * @param {object} field - The field data.
 * @returns {Promise<object>} The created field.
 */
const createPersonCustomField = async (field) => {
    const {error, data} = await supabase.from("CustomFields").insert(field).select().single();

    if(error){
        if(error.message.includes("duplicate key value violates unique constraint")){
            Toast.show({
                type: 'error',
                text2: `A field with the name "${field.name}" already exists! Try using a different name.`
            });
        }
        else{
            ErrorHandler(Error(error.message), {mutation: "createPersonCustomField", input: field, ...error}, `Something went wrong creating that field`)
        }

        throw Error()
    }

    Toast.delayShow({
        type: 'success',
        text2: `New field ${field.name} has been added successfully for People!`
    });

    return data;
}

/**
 * Updates an existing person custom field.
 * @param {object} field - The updated field data.
 * @returns {Promise<object>} The updated field.
 */
const updatePersonCustomField = async (field) => {
    const {error, data} = await supabase.from("CustomFields").update(field).eq("id", field.id).select().single();

    if(error){
        if(error.message.includes("duplicate key value violates unique constraint")){
            Toast.show({
                type: 'error',
                text2: `A field with the name "${field.name}" already exists! Try using a different name.`
            });
        }
        else if(error.code === "PGRST116"){
            Toast.show({
                type: 'error',
                text2: `That field could not be found. It may have been deleted.`
            });
        }
        else{
            ErrorHandler(Error(error.message), {mutation: "updatePersonCustomField", input: field, ...error}, `Something went wrong updating that field`)
        }
       
        throw Error()
    }

    Toast.delayShow({
        type: 'success',
        text2: `Field was successfully updated!`
    });

    return data;
}

/**
 * Saves a person custom field section (create or update).
 * @param {object} details - Section details.
 * @returns {Promise<object>} The created or updated section.
 */
const savePersonCustomFieldSection = async (details) => {
    const orgID = await getCurrentOrgID(); 

    let section = {
        id: details.id,
        name: details.name,
        position: details.position !== undefined ? details.position : 1000,
        type: 'People',
        organizationID: orgID
    }

    if(!section.id){
        return createPersonCustomFieldSection(section, details.Fields); 
    }
    else{
        return updatePersonCustomFieldSection(section, details.Fields);
    }
}

/**
 * Creates a new person custom field section.
 * @param {object} section - Section data.
 * @param {object[]} [fields=[]] - Optional fields to add.
 * @returns {Promise<object>} The created section.
 */
const createPersonCustomFieldSection = async (section, fields = []) => {
    const {data, error} = await supabase.from("CustomFieldSections").insert(section).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createPersonCustomFieldSection", input: section, ...error}, `Something went wrong creating that section`)
        throw Error()
    }

    //Update the fields to have the new sectionID
    if(fields && fields.length > 0){
        for(const field of fields){
            const {error: fieldError} = await supabase.from("CustomFields").update({sectionID: data.id, position: field.position}).eq('id', field?.id);

            if(error){
                ErrorHandler(Error(fieldError.message), {mutation: "createPersonCustomFieldSection", input: section, ...fieldError}, `Something went wrong updating the fields for that new section, you might check them, and manually update the fields to have the new section`)
                throw Error()
            }   
        }
    }

    Toast.show({
        type: 'success',
        text2: `New section ${section.name} has been added successfully!`
    });

    return data;
}

/**
 * Updates an existing person custom field section.
 * @param {object} section - Section data.
 * @param {object[]} [fields=[]] - Optional fields to update.
 * @returns {Promise<object>} The updated section.
 */
const updatePersonCustomFieldSection = async (section, fields = []) => {
    const {error, data} = await supabase.from("CustomFieldSections").update(section).eq("id", section.id).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updatePersonCustomFieldSection", input: section, ...error}, `Something went wrong updating that section`)
        throw Error()
    }

    //Update any new fields to have the updated sectionID
    if(fields && fields.length > 0){
        for(const field of fields){
            const {error: fieldError} = await supabase.from("CustomFields").update({sectionID: data.id, position: field.position}).eq('id', field?.id);

            if(fieldError){
                ErrorHandler(Error(fieldError.message), {mutation: "updatePersonCustomFieldSection", input: section, ...fieldError}, `Something went wrong updating the fields for that new section, you might check them, and manually update the fields to have the new section`)
                throw Error()
            }   
        }
    }

    Toast.show({ type: 'success', text2: `Section was successfully updated!`});
    return data;
}

/**
 * Deletes a person custom field by ID (soft delete).
 * @param {string} id - The ID of the field to delete.
 * @returns {Promise<void>}
 */
const deletePersonCustomField = async (id) => {
    const {error} = await supabase.from("CustomFields").update({isDeleted: true}).eq("id", id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deletePersonCustomField", id: id, ...error}, `Something went wrong deleting that field`)
        throw Error()
    }

    Toast.show({
        type: 'success',
        text2: `The field was deleted successfully`
    });
}

/**
 * Deletes a person custom field section.
 * @param {string} sectionID - Section ID to delete.
 * @returns {Promise<void>}
 */
const deletePersonCustomFieldSection = async (sectionID) => {
    const {error} = await supabase.from("CustomFieldSections").delete().eq("id", sectionID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deletePersonCustomFieldSection", input: sectionID, ...error}, `Something went wrong deleting that section`)
        throw Error()
    }

    Toast.show({
        type: 'success',
        text2: `The section was deleted successfully`
    });
}

/**
 * Archives a person in the database.
 * @param {object} params - The parameters for the function.
 * @param {object} params.person - The person to archive.
 * @returns {Promise<void>}
 */
const archivePerson = async (personID) => {
    if(!personID){
        Toast.show({type: 'error', text2: 'Unable to archive: person is still loading'});
        throw Error("Cannot archive person without a personID");
    }

    // Capture the name before archiving for the activity feed
    const {data: existing} = await supabase.from("People").select('firstName, lastName').eq("id", personID).maybeSingle();

    const {error} = await supabase.rpc("archive_person", {person_id: personID});
    if(error){
        ErrorHandler(Error(error.message), {mutation: "archivePerson", input: personID, ...error}, `Something went wrong archiving that person`)
        throw Error()
    }

    insertActivity({
        subjectType: 'Person',
        subjectID: personID,
        subjectName: `${existing?.firstName ?? ''} ${existing?.lastName ?? ''}`.trim(),
        verb: 'update',
        details: 'Archived',
    }).catch(() => {});
}

/**
 * Bulk updates scalar and custom field values for multiple people.
 * @param {string[]} personIds - IDs of the people to update.
 * @param {object} scalarPatch - People column patches (e.g. gender, dateOfBirth, invitedByID, invitedByNote).
 * @param {object} customPatch - Custom field value patches keyed by custom field ID.
 * @returns {Promise<object>} RPC result.
 */
const bulkUpdatePeople = async ({ personIds, scalarPatch = {}, customPatch = {} }) => {
    const { data, error } = await supabase.rpc('bulk_update_people', {
        person_ids: personIds,
        scalar_patch: scalarPatch,
        // Custom-field writes are paid-only — drop the patch on Free so the
        // server's explicit plan rejection is never needed.
        custom_patch: customFieldsEnabled() ? customPatch : {},
    });

    if(error){
        ErrorHandler(Error(error.message), {mutation: "bulkUpdatePeople", input: {personIds, scalarPatch, customPatch}, ...error}, `Something went wrong updating those people`)
        throw Error()
    }

    return data;
}

/**
 * Saves a person (create or update) with family and roles.
 * @param {import('../types/Person').Person} details - Person details.
 * @returns {Promise<import('../types/Person').Person>} The created or updated person.
 */
const savePerson = async (details) => {
    const orgID = await getCurrentOrgID();

    const phones = (details.Phones || []).filter(p => p.phoneNumber?.trim());

    // Self-edit: user updating their own profile without 'Edit People'.
    // Takes a restricted path — the People self-edit trigger only allows a
    // whitelisted set of columns to change on your own row.
    if(details.selfEdit && details.id){
        return updatePersonSelf(details, phones, orgID);
    }

    let newFamily = details.Family ? details.Family : [];
    // Dynamic roles are derived from their filter — never written to RolePeople.
    let newRoles = (details.Roles || []).filter(r => !r?.isDynamicRole && !r?.quickFilterID);

    const personID = details.id || Crypto.randomUUID();
    // customFieldValues is a paid-only column — it never travels inside the
    // People insert/update payload. Paid orgs write it through
    // update_person_custom_fields after the row write; Free orgs skip it
    // entirely so existing server-side values are preserved untouched.
    const allowCustomFields = customFieldsEnabled();
    const customFieldValues = allowCustomFields ? await manageFileCustomFields(details.customFieldValues, personID) : null;
    const originalValues = details.originalValues || null;


    let person = {
        addressID: details.addressID,
        // Explicit null (cleared in the form) must reach the DB — undefined
        // keys are stripped from the update payload and the old value stays.
        dateOfBirth: details.dateOfBirth === undefined ? undefined : (details.dateOfBirth ? (typeof details.dateOfBirth == 'string' ? details.dateOfBirth : details.dateOfBirth?.toLocaleDateString()) : null),
        email: details.email?.trim(),
        familyRole: details.familyRole,
        firstName: details.firstName?.trim(),
        gender: details.gender,
        id: personID,
        invitedByID: !details.invitedByID ? null : details.invitedByID,
        invitedByNote: details.invitedByNote?.trim() || null,
        isArchived: details.isArchived,
        lastName: details.lastName?.trim(),
        organizationID: orgID,
    }

    const saved = !details.id
        ? await createPerson(person, newRoles, newFamily, phones, orgID)
        : await updatePerson(person, newRoles, newFamily, phones, orgID, originalValues, details);

    if (customFieldValues && Object.keys(customFieldValues).length > 0) {
        const applied = await updatePersonCustomFieldValues(saved?.id || personID, customFieldValues);
        if (applied === false) {
            throw Error('The person was saved, but the custom fields could not be updated');
        }
        if (saved && applied !== true) saved.customFieldValues = applied;
    }

    return saved;
}

/**
 * Creates a new person with roles.
 * @param {object} person - Person details.
 * @param {object[]} [roles=[]] - Roles.
 * @returns {Promise<object>} The created person.
 */
const createPerson = async (person, roles = [], family = {}, phones = [], orgID = null) => {
    if(!orgID) orgID = await getCurrentOrgID();

    const {error, data: newPerson} = await supabase.from("People").insert(person).select(PEOPLE_SELECT).single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createPerson", input: person, ...error}, `Something went wrong creating that person`)
        throw Error()
    }
    stripCustomFieldsForFree(newPerson);

    const errors = []

    if(phones.length > 0){
        const {error: phoneError} = await supabase.from("PeoplePhones").insert(
            phones.map(p => ({ personID: newPerson.id, organizationID: orgID, phoneNumber: p.phoneNumber.trim(), label: p.label || null, smsOptInStatus: p.smsOptInStatus || 'never_opted_in', private: p.private || false }))
        );
        if(phoneError) errors.push(phoneError);

        // Sync smsOptInStatus across all PeoplePhones with the same phone number in the organization
        // This ensures consistency for family scenarios where spouses/children share a phone
        for (const phone of phones.filter(phone => phone.smsOptInStatus && phone.smsOptInStatus !== 'never_opted_in')) {
            const normalizedPhone = phone.phoneNumber.trim();
            const { error: syncError } = await supabase
                .from("PeoplePhones")
                .update({ smsOptInStatus: phone.smsOptInStatus || 'never_opted_in' })
                .eq("organizationID", orgID)
                .eq("phoneNumber", normalizedPhone);
            
            if (syncError) {
                console.error(`Failed to sync smsOptInStatus for phone ${normalizedPhone}:`, syncError);
            }
        }
    }

    if(roles.length > 0){
        const {error} = await supabase.from("RolePeople").insert(roles.map(r => ({organizationID: orgID, personID: newPerson.id, roleID: r.id})).flat());
        if(error) errors.push(error);
    }

    if(errors.length > 0){
        ErrorHandler(Error("One of more errors encountered creating a person"), {mutation: "createPerson", input: {roles}, errors: errors}, `We created the person, but something went wrong creating the Roles for them`)
        throw Error()
    }

    if(family && family?.FamilyMembers?.length > 0){ 
        const familyID = family.familyID ? family.familyID : (await createFamily()).id;

        for(const member of family.FamilyMembers){
            const updatedFamily = await supabase.from("People").update({familyID, familyRole: member.familyRole}).eq('id', member.id)

            if(updatedFamily.error){
                ErrorHandler(Error("One of more errors encountered creating a person"), {mutation: "createPerson", input: {roles}, errors: updatedFamily.error}, `We created the person, but something went wrong creating the family members for them`)
                throw Error()
            }
        }

        const updatedNewPerson = await supabase.from("People").update({familyID, familyRole: person.familyRole || 'Unassigned'}).eq('id', newPerson.id)

        if(updatedNewPerson.error){
            ErrorHandler(Error("One of more errors encountered creating a person"), {mutation: "createPerson", input: {family}, errors: updatedNewPerson.error}, `We created the person, but something went wrong assigning them to the family`)
            throw Error()
        }
    }

    Toast.show({
        type: 'success',
        text2: `${person.firstName} ${person.lastName} has been added successfully!`
    });

    sendBreezeNewPerson(newPerson);

    insertActivity({
        subjectType: 'Person',
        subjectID: newPerson.id,
        subjectName: `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim(),
        verb: 'create',
    }).catch(() => {});

    return newPerson;
}

/**
 * Manages file custom fields for a person (uploads files if needed).
 * @param {object} customFieldValues - Custom field values.
 * @param {string} personID - Person ID.
 * @returns {Promise<object>} Updated custom field values.
 */
const manageFileCustomFields = async (customFieldValues, personID) => {

    for (const fieldID in customFieldValues) {
        const value = customFieldValues[fieldID];

        // Handle array of files
        if(Array.isArray(value)){
            const processedFiles = [];
            for(const file of value){
                if(file && typeof file == 'object' && file.uri){
                    const orgID = await getCurrentOrgID();
                    const key = `${orgID}/people/${personID}/customFields/${fieldID}/${file.name}`;
                    const result = await saveFile(file.uri, key, false, file.mimeType);
                    if(result){
                        // Only store key and filename
                        processedFiles.push({
                            key: key,
                            filename: file.name || file.filename
                        });
                    } else {
                        // Upload failed (e.g. stale local URI) — keep original value so no data is lost
                        processedFiles.push(file);
                    }
                } else {
                    // Keep as-is if no uri (already processed or URL string)
                    processedFiles.push(file);
                }
            }
            customFieldValues[fieldID] = processedFiles;
        }
        // Handle single file object
        else if(value && typeof value == 'object' && value.uri){
            const orgID = await getCurrentOrgID();
            const key = `${orgID}/people/${personID}/customFields/${fieldID}/${value.name}`;
            const result = await saveFile(value.uri, key, false, value.mimeType);
            if(result){
                // Only store key and filename, strip out base64 and other unnecessary data
                customFieldValues[fieldID] = {
                    key: key,
                    filename: value.name || value.filename
                };
            }
            // Upload failed (e.g. stale local URI) — leave original value so no data is lost
        }
    }

    return customFieldValues;
}

/**
 * Updates a person and their roles.
 * @param {object} person - Person details.
 * @param {object[]} [roles=[]] - Roles.
 * @returns {Promise<object>} The updated person.
 */
const updatePerson = async (person, roles = [], family = {}, phones = [], orgID = null, originalValues = null, details = null) => {
    if(!orgID) orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from("People").update(person).eq("id", person.id).select(`${PEOPLE_SELECT}, Roles(id), Family:Families(id, FamilyMembers:People(id, familyRole))`).maybeSingle();
    stripCustomFieldsForFree(data);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updatePerson", input: person, ...error}, `Something went wrong updating that person`)
        throw Error;
    }

    const {itemsToAdd: rolesToAdd, itemsToDelete: rolesToRemove} = findAssociativeChanges(roles, data.Roles);

    //Role changes — guard against empty arrays to avoid PostgREST errors on no-op saves
    let deleteError2 = null;
    let insertError2 = null;
    let insertData2 = data.Roles;

    if(rolesToRemove?.length > 0){
        ({error: deleteError2} = await supabase.from("RolePeople").delete().eq("personID", person.id).in("roleID", rolesToRemove.map(r => r.id)));
    }

    if(rolesToAdd?.length > 0){
        ({error: insertError2, data: insertData2} = await supabase.from("RolePeople").insert(rolesToAdd.map(r => ({organizationID: orgID, personID: person.id, roleID: r.id}))));
    }

    if(deleteError2 || insertError2){
        ErrorHandler(Error(deleteError2?.message || insertError2?.message), {mutation: "updatePerson", input: {roles}, ...(deleteError2 || insertError2)}, `We updated the person, but something went wrong updating the roles for them`)
        throw Error();
    }


    if(family && family?.FamilyMembers?.length > 0){ 
        const familyID = family.familyID ? family.familyID : (await createFamily()).id;
        const existingFamilyMemberIDsToDelete = data.Family?.FamilyMembers?.filter(m => !family.FamilyMembers.some(fm => fm.id == m.id)).map(m => m.id);
        const errors = [];

        for(const member of family.FamilyMembers){
            const updatedFamily = await supabase.from("People").update({familyID, familyRole:member.familyRole}).eq('id', member.id)

            if(updatedFamily.error) errors.push(updatedFamily.error);
        }

        if(existingFamilyMemberIDsToDelete){
            for(const memberID of existingFamilyMemberIDsToDelete){
                const updatedFamily = await supabase.from("People").update({familyID: null, familyRole: null}).eq('id', memberID)

                if(updatedFamily.error) errors.push(updatedFamily.error);
            }
        }

        const updatedThisPerson = await supabase.from("People").update({familyID, familyRole: person.familyRole || 'Unassigned'}).eq('id', person.id)

        if(updatedThisPerson.error) errors.push(updatedThisPerson.error);

        if(errors.length > 0){
            ErrorHandler(Error("One of more errors encountered updating a person"), {mutation: "updatePerson", input: {family}, errors: errors}, `We updated the person, but something went wrong updating some family members`)
            throw Error();
        }
    }
    
    // Sync PeoplePhones: delete all existing then re-insert
    await supabase.from("PeoplePhones").delete().eq("personID", person.id);
    if(phones.length > 0){
        const uniquePhones = phones.filter((p, i, arr) =>
            i === arr.findIndex(t => t.phoneNumber.trim() === p.phoneNumber.trim())
        );
        const {error: phoneError} = await supabase.from("PeoplePhones").insert(
            uniquePhones.map(p => ({ personID: person.id, organizationID: orgID, phoneNumber: p.phoneNumber.trim(), label: p.label || null, smsOptInStatus: p.smsOptInStatus || 'never_opted_in', private: p.private || false }))
        );
        if(phoneError){
            ErrorHandler(Error(phoneError.message), {mutation: "updatePerson", input: {phones}, ...phoneError}, `We updated the person, but something went wrong updating the phone numbers`);
        }

        // Sync smsOptInStatus across all PeoplePhones with the same phone number in the organization
        // This ensures consistency for family scenarios where spouses/children share a phone
        for (const phone of phones) {
            const normalizedPhone = phone.phoneNumber.trim();
            const { error: syncError } = await supabase
                .from("PeoplePhones")
                .update({ smsOptInStatus: phone.smsOptInStatus || 'never_opted_in' })
                .eq("organizationID", orgID)
                .eq("phoneNumber", normalizedPhone);
            
            if (syncError) {
                console.error(`Failed to sync smsOptInStatus for phone ${normalizedPhone}:`, syncError);
            }
        }
    }

    Toast.delayShow({type: 'success',text2: `${person.firstName} ${person.lastName} has been updated successfully!`});

    // Log activity (fire and forget). The detail-building queries below are
    // non-critical — the person is already updated — so wrap them so a network
    // blip here can't fail the mutation, trigger retries (which re-fire the
    // toast above and prevent the modal from closing), or block onSuccess.
    try {
        const formatDate = (d) => {
            if(!d) return null;
            const date = typeof d === 'string' ? new Date(d) : d;
            return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString();
        };
        const resolvePersonName = (id, oldObj, newObj) => {
            const person = newObj?.InvitedBy || oldObj?.InvitedBy;
            return person ? `${person.firstName} ${person.lastName}`.trim() : null;
        };
        let scalarDetails = originalValues ? buildUpdateDetails(originalValues, details || person, {
            firstName: { label: 'First Name' },
            lastName: { label: 'Last Name' },
            email: { label: 'Email' },
            gender: { label: 'Gender' },
            dateOfBirth: { label: 'Date of Birth', format: formatDate },
            familyRole: { label: 'Family Role' },
            invitedByID: { label: 'Invited By', format: resolvePersonName },
        }) : null;

        // Resolve custom field names and diff them. The values live on
        // `details` (the form payload) — `person` never carries
        // customFieldValues because the column only moves through the paid
        // RPC channel.
        const customFieldIds = [...new Set([
            ...Object.keys(originalValues?.customFieldValues || {}),
            ...Object.keys(details?.customFieldValues || {}),
        ])];
        let customFieldDetails = null;
        if (customFieldIds.length > 0 && customFieldsEnabled()) {
            const { data: cfRows } = await supabase.from('CustomFields').select('id, name').in('id', customFieldIds).eq('organizationID', orgID);
            const cfNameMap = Object.fromEntries((cfRows || []).map(cf => [cf.id, cf.name]));
            customFieldDetails = buildCustomFieldDetails(
                originalValues?.customFieldValues,
                details?.customFieldValues,
                cfNameMap,
            );
        }
        scalarDetails = joinDetails(scalarDetails, customFieldDetails);

        // Resolve role names for added/removed roles (no IDs in details)
        const changedRoleIDs = [...new Set([...(rolesToAdd || []), ...(rolesToRemove || [])].map(r => r.id).filter(Boolean))];
        let roleNameByID = {};
        if(changedRoleIDs.length > 0){
            const {data: roleRows} = await supabase.from("Roles").select('id, name').in('id', changedRoleIDs);
            roleNameByID = Object.fromEntries((roleRows || []).map(r => [r.id, r.name]));
        }
        const roleDetails = [
            ...(rolesToAdd || []).filter(r => roleNameByID[r.id] || r.name).map(r => `- Added role '${roleNameByID[r.id] || r.name}'`),
            ...(rolesToRemove || []).filter(r => roleNameByID[r.id] || r.name).map(r => `- Removed role '${roleNameByID[r.id] || r.name}'`),
        ];

        insertActivity({
            subjectType: 'Person',
            subjectID: person.id,
            subjectName: `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim(),
            verb: 'update',
            details: joinDetails(scalarDetails, roleDetails),
        }).catch(() => {});
    } catch (e) {
        __DEV__ && console.error('Failed to build activity details for person update:', e);
    }

    return {...data, Roles: insertData2};
}

/**
 * Restricted self-update for a user editing their own profile without
 * 'Edit People' permission. Only whitelisted columns are sent — the People
 * self-edit trigger rejects changes to anything else. No role sync, no
 * family sync, no custom field handling.
 * @param {object} details - Person details from the edit screen.
 * @param {object[]} [phones=[]] - Phone numbers to sync.
 * @param {string} [orgID] - Organization ID.
 * @returns {Promise<object>} The updated person.
 */
const updatePersonSelf = async (details, phones = [], orgID = null) => {
    if(!orgID) orgID = await getCurrentOrgID();

    const person = {
        id: details.id,
        firstName: details.firstName?.trim(),
        lastName: details.lastName?.trim(),
        gender: details.gender,
        // Explicit null (cleared in the form) must reach the DB — undefined
        // keys are stripped from the update payload and the old value stays.
        dateOfBirth: details.dateOfBirth === undefined ? undefined : (details.dateOfBirth ? (typeof details.dateOfBirth == 'string' ? details.dateOfBirth : details.dateOfBirth?.toLocaleDateString()) : null),
        email: details.email?.trim(),
        addressID: details.addressID,
    };

    const {error, data} = await supabase.from("People").update(person).eq("id", person.id).select(`${PEOPLE_SELECT}, Roles(id), Family:Families(id, FamilyMembers:People(id, familyRole))`).maybeSingle();
    stripCustomFieldsForFree(data);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updatePersonSelf", input: person, ...error}, `Something went wrong updating your profile`)
        throw Error;
    }

    // Sync PeoplePhones: delete all existing then re-insert
    await supabase.from("PeoplePhones").delete().eq("personID", person.id);
    if(phones.length > 0){
        const uniquePhones = phones.filter((p, i, arr) =>
            i === arr.findIndex(t => t.phoneNumber.trim() === p.phoneNumber.trim())
        );
        const {error: phoneError} = await supabase.from("PeoplePhones").insert(
            uniquePhones.map(p => ({ personID: person.id, organizationID: orgID, phoneNumber: p.phoneNumber.trim(), label: p.label || null, smsOptInStatus: p.smsOptInStatus || 'never_opted_in', private: p.private || false }))
        );
        if(phoneError){
            ErrorHandler(Error(phoneError.message), {mutation: "updatePersonSelf", input: {phones}, ...phoneError}, `We updated your profile, but something went wrong updating the phone numbers`);
        }

        // Sync smsOptInStatus across all PeoplePhones with the same phone number in the organization
        // This ensures consistency for family scenarios where spouses/children share a phone
        for (const phone of phones) {
            const normalizedPhone = phone.phoneNumber.trim();
            const { error: syncError } = await supabase
                .from("PeoplePhones")
                .update({ smsOptInStatus: phone.smsOptInStatus || 'never_opted_in' })
                .eq("organizationID", orgID)
                .eq("phoneNumber", normalizedPhone);

            if (syncError) {
                console.error(`Failed to sync smsOptInStatus for phone ${normalizedPhone}:`, syncError);
            }
        }
    }

    Toast.delayShow({type: 'success',text2: `${person.firstName} ${person.lastName} has been updated successfully!`});

    // Log activity (fire and forget) — wrapped so a failure here can't fail
    // the mutation or block onSuccess.
    try {
        const formatDate = (d) => {
            if(!d) return null;
            const date = typeof d === 'string' ? new Date(d) : d;
            return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString();
        };
        const activityDetails = details.originalValues ? buildUpdateDetails(details.originalValues, details, {
            firstName: { label: 'First Name' },
            lastName: { label: 'Last Name' },
            email: { label: 'Email' },
            gender: { label: 'Gender' },
            dateOfBirth: { label: 'Date of Birth', format: formatDate },
        }) : null;

        insertActivity({
            subjectType: 'Person',
            subjectID: person.id,
            subjectName: `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim(),
            verb: 'update',
            details: activityDetails,
        }).catch(() => {});
    } catch (e) {
        __DEV__ && console.error('Failed to build activity details for self person update:', e);
    }

    return data;
}

/**
 * Finds existing people whose first + last name match exactly (case-insensitive).
 * Used to flag potential duplicates while creating a new person.
 * @param {string} firstName
 * @param {string} lastName
 * @returns {Promise<object[]>} Matching people (lightweight fields).
 */
const findPeopleByName = async (firstName, lastName) => {
    const first = firstName?.trim();
    const last = lastName?.trim();
    if(!first || !last) return [];

    const {data, error} = await supabase
        .from("People")
        .select("id, firstName, lastName, email, dateOfBirth, avatarUpdatedAt, avatarStorageKey, isArchived")
        .ilike("firstName", first)
        .ilike("lastName", last)
        .limit(10);

    if(error){
        ErrorHandler(Error(error.message), {query: "findPeopleByName", firstName: first, lastName: last, error});
        return [];
    }

    return data || [];
};

/**
 * Finds potential duplicate people based on matching name or email.
 * @returns {Promise<object[]>} List of duplicate pairs.
 */
const getPotentialDuplicates = async () => {
    const { data, error } = await supabase.rpc('find_potential_duplicates');

    if (error) {
        ErrorHandler(Error(error.message), { query: "getPotentialDuplicates", error });
        throw error;
    }

    return data.map(row => ({
        person1: row.person1,
        person2: row.person2,
        reasons: row.reasons
    }));
};

const ignorePotentialDuplicate = async ({ person1ID, person2ID }) => {
    const organizationID = await getCurrentOrgID();
    const [firstID, secondID] = [person1ID, person2ID].sort();
    const { error } = await supabase.from('IgnoredPeopleDuplicates').upsert({
        organizationID,
        person1ID: firstID,
        person2ID: secondID,
    }, { onConflict: 'organizationID,person1ID,person2ID', ignoreDuplicates: true });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'ignorePotentialDuplicate', error },
            'Could not ignore this pair. Please try again.');
        throw error;
    }
};

/**
 * Merges two people records.
 * @param {object} params - The parameters for the function.
 * @param {object} params.newPerson - The new/merged person data.
 * @param {object} params.associations - Field associations.
 * @param {object} params.firstPerson - The person to keep.
 * @param {object} params.secondPerson - The person to archive.
 * @returns {Promise<void>}
 */
const mergePeople = async ({newPerson, associations, firstPerson, secondPerson}) => {
    const old_id = secondPerson.id;
    const new_id = firstPerson.id;
    const deleteOldIDs = [];
    const deleteNewIDs = [];

    for(const field in associations){
        if(associations[field] == new_id){
            deleteOldIDs.push(field);
        }
        else if(associations[field] == old_id){
            deleteNewIDs.push(field);
        }
    }

    // customFieldValues is paid-only — it moves through the RPC channel, not
    // the People row update. Free orgs skip it entirely so the surviving
    // profile's stored values are preserved.
    const { customFieldValues: mergedCustomFieldValues, ...personFields } = newPerson;

    const newPersonUpdate = await supabase.from("People").update(personFields).eq("id", new_id).select('id').single();

    if(newPersonUpdate.error){
        ErrorHandler(Error(newPersonUpdate.error.message), {mutation: "mergePeople-update", input: {...newPerson}, error: newPersonUpdate.error}, `Something went wrong merging these people`)
        throw Error
    }

    if (customFieldsEnabled() && mergedCustomFieldValues && Object.keys(mergedCustomFieldValues).length > 0) {
        const applied = await updatePersonCustomFieldValues(new_id, mergedCustomFieldValues);
        if (applied === false) {
            ErrorHandler(Error('Custom field merge failed'), {mutation: "mergePeople-customFields", input: {new_id}}, `Something went wrong merging these people`);
            throw Error;
        }
    }

    const relationUpdate = await supabase.rpc("update_person_relations", {new_id, old_id, delete_old_id_tables: deleteOldIDs, delete_new_id_tables: deleteNewIDs});

    if(relationUpdate.error){
        ErrorHandler(Error(relationUpdate.error.message), {mutation: "mergePeople-relations", input: {...newPerson}, error: relationUpdate.error}, `Something went wrong merging these people`)
        throw Error
    }

    await archivePerson(secondPerson.id);
}

/**
 * Creates a new family.
 * @param {object[]} members - The members of the family.
 * @returns {Promise<object>} The created family.
 */
const createFamily = async (members = []) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from("Families").insert({organizationID: orgID}).select().maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createFamily", input: {members}, ...error}, `Something went wrong creating that family`)
        throw Error()
    }

    for(const member of members){
        const familyUpdate = await supabase.from("People").update({familyID: data.id, familyRole: member.familyRole}).eq("id", member.personID);

        if(familyUpdate.error){
            ErrorHandler(Error(familyUpdate.error.message), {mutation: "createFamilyUpdateMembers", input: {members}, error: familyUpdate.error}, `Something went wrong creating that family`)
            throw Error()
        }
    }

    return data;
}


/**
 * Retrieves all addresses for the current org.
 * @returns {Promise<object[]>} List of addresses.
 */
const getAddresses = async () => {
    const orgID = await getCurrentOrgID(); 

    const {data, error} = await supabase.from("Addresses").select("*").eq("organizationID", orgID);

    if(error){
        ErrorHandler(Error(error.message), {query: "getAddresses", orgID, ...error}, `Something went wrong fetching the addresses`)
        return [];
    }

    return data;
}

/**
 * Retrieves an address by ID.
 * @param {object} params - The parameters for the function.
 * @param {string} params.id - The ID of the address.
 * @returns {Promise<object>} The address.
 */
const getAddress = async ({id}) => {
    const orgID = await getCurrentOrgID(); 

    const {data, error} = await supabase.from("Addresses").select("*, HouseholdMembers:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey, isArchived), Locations(id, name, detailedArea)").eq("organizationID", orgID).eq("id", id).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {query: "getAddress", orgID, id, ...error}, `Something went wrong fetching the address`)
        return {};
    }

    return data;
}

/**
 * Saves an address (create or update).
 * @param {object} params - The parameters for the function.
 * @param {object} params.details - Address details.
 * @returns {Promise<object>} The created or updated address.
 */
const saveAddress = async ({details, originalValues}) => {
    details.organizationID = await getCurrentOrgID();

    const isEdit = !!details.id;
    let data, error;

    // Remove relation fields from details as they're not columns in the Addresses table
    const { HouseholdMembers, Locations, ...addressDetails } = details;
    void HouseholdMembers;
    void Locations;

    if (isEdit) {
        const { id, organizationID, ...updateFields } = addressDetails;
        ({ data, error } = await supabase.from("Addresses").update(updateFields).eq("id", id).eq("organizationID", organizationID).select().maybeSingle());
    } else {
        ({ data, error } = await supabase.from("Addresses").insert(addressDetails).select().maybeSingle());
    }

    if(error){
        ErrorHandler(Error(error.message), {mutation: "saveAddress", input: details, ...error}, `Something went wrong ${isEdit ? "updating" : "creating"} that address`)
        throw Error()
    }

    // Log activity (fire and forget)
    const addressName = [details.street, details.city].filter(Boolean).join(', ') || 'Address';
    if(isEdit){
        const addrDetails = originalValues ? buildUpdateDetails(originalValues, addressDetails, {
            street: { label: 'Street' },
            city: { label: 'City' },
            state: { label: 'State' },
            zip: { label: 'Zip' },
            country: { label: 'Country' },
        }) : null;
        insertActivity({ subjectType: 'Address', subjectID: data.id, subjectName: addressName, verb: 'update', details: addrDetails }).catch(() => {});
    } else {
        insertActivity({ subjectType: 'Address', subjectID: data.id, subjectName: addressName, verb: 'create' }).catch(() => {});
    }

    await supabase.functions.invoke('geocode-address', {
        body: {
            addressID: data.id,
            address: details.street + " " + details.city + ", " + details.state + " " + details.zip
        }
    });

    Toast.show({
        type: 'success',
        text2: `Address has been successfully ${isEdit ? "updated" : "created"}`
    });

    return data;
}

/**
 * Retrieves all protected person fields for the current org.
 * @returns {Promise<object[]>} List of protected person fields.
 */
const getProtectedPersonFields = async () => {
    const orgID = await getCurrentOrgID(); 

    const {data, error} = await supabase.from("ProtectedPersonFields").select("*").eq("organizationID", orgID);

    if(error){
        ErrorHandler(Error(error.message), {query: "getProtectedPersonFields", orgID, ...error}, `Something went wrong fetching your organizations protected fields`)
        return [];
    }

    return data;
}

/**
 * Fetches full person objects for a specific set of person IDs.
 * Used by batch actions (Email, SMS, Add to Role) on tables that reference
 * people indirectly (e.g. form entries with LinkedPerson).
 * @param {string[]} ids - Person IDs to fetch
 * @param {string[]} [returnFields] - Fields to return (default: all)
 * @returns {Promise<object[]>} Array of matching person objects
 */
const getPeopleByIds = async (ids, returnFields) => {
    if (!ids?.length) return [];
    const orgID = await getCurrentOrgID();
    // customFieldValues has no browser column grant — naming it in a select
    // errors outright, so strip it from any caller-provided field list. `*`
    // expands against table privileges and fails with 42501 on every plan,
    // so always expand it to the grant-safe column list instead.
    const SAFE_PEOPLE_SELECT = 'id, firstName, lastName, email, avatarUpdatedAt, avatarStorageKey, Phones:PeoplePhones(phoneNumber, smsOptInStatus), Address:Addresses(street, streetLine2, city, state, zip)';
    const fields = returnFields ? returnFields.filter(f => f !== 'customFieldValues') : null;
    const selectFields = !fields
        ? SAFE_PEOPLE_SELECT
        : fields.flatMap(f => f === '*' ? [PEOPLE_SELECT] : [f]).join(', ');
    const { data, error } = await supabase
        .from('People')
        .select(selectFields)
        .eq('organizationID', orgID)
        .in('id', ids);
    if (error) {
        ErrorHandler(Error(error.message), { ids, error }, 'Something went wrong fetching people by IDs');
        return [];
    }
    return data || [];
};

/**
 * Returns the subset of the given person IDs that are Reach users (have a
 * RegisteredUsersOrganizations row for the current org). Reach users cannot
 * be archived, so bulk actions use this to skip them.
 * @param {string[]} ids - Person IDs to check
 * @returns {Promise<string[]>} Person IDs that are Reach users in this org
 */
const getReachUserIdsAmong = async (ids) => {
    if (!ids?.length) return [];
    const orgID = await getCurrentOrgID();
    const { data, error } = await supabase
        .from('RegisteredUsersOrganizations')
        .select('personID')
        .eq('organizationID', orgID)
        .in('personID', ids);
    if (error) {
        ErrorHandler(Error(error.message), { ids, error }, 'Something went wrong checking which people are Reach users');
        return [];
    }
    return data?.map(r => r.personID) || [];
};

/**
 * Fetches all person IDs matching the current filters/search, excluding specific IDs.
 * Used for bulk actions in "select all" (exclude) mode to avoid operating only on
 * the client-side paginated subset.
 * @param {object} params
 * @param {string} [params.searchTerm]
 * @param {object} [params.filters={}]
 * @param {string} [params.filterLogic='AND']
 * @param {string[]} [params.excludeIds=[]] - IDs to exclude (deselected items)
 * @returns {Promise<string[]>} Array of matching person IDs
 */
const getAllPeopleIdsForSelection = async ({ searchTerm = '', filters = {}, filterLogic = 'AND', excludeIds = [] } = {}) => {
    const orgID = await getCurrentOrgID();
    const searchValue = searchTerm?.trim() || undefined;
    const sanitizedFilters = sanitizeFilterConditions(filters);

    const { data: people, error } = await supabase.rpc("filter_people", {
        filters: { ...sanitizedFilters, search: searchValue },
        organizationid: orgID,
        logictype: filterLogic,
        record_limit: 100000,
        record_offset: 0,
        return_fields: ['id']
    });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getAllPeopleIdsForSelection", filters, filterLogic }, `Something went wrong fetching all people for bulk action`);
        return [];
    }

    const results = people?.[0]?.people?.results ?? [];
    const excludeSet = new Set(excludeIds);
    return results.map(p => p.id).filter(id => !excludeSet.has(id));
};

/**
 * Fetches full person objects for all people matching the current filters/search,
 * excluding specific IDs. Used by Email/SMS/Export bulk actions in "select all" mode
 * to ensure all pages are covered, not just the client-side paginated cache.
 * @param {object} params
 * @param {string} [params.searchTerm]
 * @param {object} [params.filters={}]
 * @param {string} [params.filterLogic='AND']
 * @param {string[]} [params.excludeIds=[]] - IDs to exclude (deselected items)
 * @param {string[]} [params.returnFields] - fields to return (default: all)
 * @returns {Promise<object[]>} Array of matching person objects
 */
const getAllPeopleForSelection = async ({ searchTerm = '', filters = {}, filterLogic = 'AND', excludeIds = [], returnFields } = {}) => {
    const orgID = await getCurrentOrgID();
    const searchValue = searchTerm?.trim() || undefined;
    const sanitizedFilters = sanitizeFilterConditions(filters);

    const { data: people, error } = await supabase.rpc("filter_people", {
        filters: { ...sanitizedFilters, search: searchValue },
        organizationid: orgID,
        logictype: filterLogic,
        record_limit: 100000,
        record_offset: 0,
        ...(returnFields ? { return_fields: returnFields } : {})
    });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getAllPeopleForSelection", filters, filterLogic }, `Something went wrong fetching all people for bulk action`);
        return [];
    }

    const results = people?.[0]?.people?.results ?? [];
    const excludeSet = new Set(excludeIds);
    return results.filter(p => !excludeSet.has(p.id));
};

const VALID_FAMILY_ROLES = ['Head of Household', 'Spouse', 'Child', 'Adult', 'Unassigned'];

const normalizeImportEnum = (value, validValues) => {
    if(value == null || value === '') return null;
    const match = validValues.find((v) => v.toLowerCase() === String(value).trim().toLowerCase());
    return match || null;
}

const parseImportBoolean = (value) => {
    if(typeof value === 'boolean') return value;
    if(value == null || value === '') return false;
    return ['true', 'yes', '1', 'y', 't'].includes(String(value).trim().toLowerCase());
}

const normalizeImportDate = (value) => {
    if(!value) return null;
    const d = new Date(value);
    if(isNaN(d.getTime())) return value; // leave raw so the DB surfaces a meaningful error
    return d.toISOString().slice(0, 10);
}

const insertInChunks = async (table, rows, chunkSize = 2500) => {
    if(!rows || rows.length === 0) return;
    for(let i = 0; i < rows.length; i += chunkSize){
        const {error} = await supabase.from(table).insert(rows.slice(i, i + chunkSize));
        if(error){
            ErrorHandler(Error(error.message), {mutation: "importPeople", input: {table, error}}, `Something went wrong importing ${table.toLowerCase()}`)
            throw Error(error.message);
        }
    }
}

/**
 * Returns a People custom field section id to attach newly-created import fields to,
 * creating one if the org has none (custom fields must belong to a section).
 * @param {string} orgID - The organization id.
 * @returns {Promise<string>} A custom field section id.
 */
const getImportCustomFieldSectionID = async (orgID) => {
    const {data: sections} = await supabase.from("CustomFieldSections").select("id, position").eq("organizationID", orgID).eq("type", "People").order("position", {ascending: true});

    if(sections && sections.length > 0) return sections[0].id;

    const {data: created, error} = await supabase.from("CustomFieldSections").insert({name: "Imported Fields", position: 0, type: "People", organizationID: orgID}).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "importPeople", input: {error}}, `Something went wrong creating a section for the imported fields`)
        throw Error(error.message);
    }

    return created.id;
}

/**
 * Bulk-imports people from a mapped CSV.
 * @param {object} params - The parameters for the function.
 * @param {object[]} params.data - Parsed CSV rows.
 * @param {object} params.mappedColumns - Map of person field name -> CSV header.
 * @param {object[]} [params.newCustomFields] - New custom fields to create before importing: {header, name, type}.
 * @param {'update'|'skip'} [params.existingRecordMode] - What to do with rows matching an existing person.
 * @returns {Promise<{created:number, updated:number, skipped:number}>} Import summary.
 */
const importPeople = async ({data, mappedColumns = {}, newCustomFields = [], existingRecordMode = 'skip', customFields = []}) => {
    const orgID = await getCurrentOrgID();

    // Custom fields are paid-only. Free orgs skip them entirely — no
    // reads/writes and no new field creation. On paid orgs pick the write
    // strategy once: 'rpc' when the plan migration deployed (the column has
    // no browser grant), 'legacy' while the RPC is still missing.
    const customFieldsMode = !customFieldsEnabled()
        ? 'skip'
        : (await customFieldsRpcAvailable() ? 'rpc' : 'legacy');

    // 1) Create any new custom fields first so we can route their values.
    // Insert one at a time so a name collision with an existing field doesn't
    // abort the whole batch — we just reuse the existing field instead.
    const newFieldIdByHeader = {}; // csvHeader -> custom field id
    if(newCustomFields.length > 0 && customFieldsMode !== 'skip'){
        const sectionID = await getImportCustomFieldSectionID(orgID);

        // Fetch existing field names for this org/module so we can skip duplicates.
        const {data: existingFields} = await supabase.from("CustomFields").select("id, name").eq("organizationID", orgID).eq("module", "People").eq("isDeleted", false);
        const existingByName = (existingFields || []).reduce((acc, cf) => { acc[cf.name.toLowerCase()] = cf; return acc; }, {});

        for(const f of newCustomFields){
            const name = (f.name?.trim() || f.header);
            const existing = existingByName[name.toLowerCase()];

            if(existing){
                newFieldIdByHeader[f.header] = existing.id;
                continue;
            }

            const {data: created, error: createError} = await supabase.from("CustomFields").insert({
                name,
                type: f.type || 'Text',
                module: 'People',
                sectionID,
                options: [],
                organizationID: orgID,
            }).select("id, name").single();

            if(createError){
                if(createError.message.includes("duplicate key value violates unique constraint")){
                    // Race or we missed it — refetch and reuse.
                    const {data: refetched} = await supabase.from("CustomFields").select("id, name").eq("organizationID", orgID).eq("module", "People").eq("name", name).maybeSingle();
                    if(refetched){
                        newFieldIdByHeader[f.header] = refetched.id;
                        existingByName[name.toLowerCase()] = refetched;
                        continue;
                    }
                }
                ErrorHandler(Error(createError.message), {mutation: "importPeople", input: {field: f, error: createError}}, `Something went wrong creating the custom field "${name}"`)
                throw Error(createError.message);
            }

            newFieldIdByHeader[f.header] = created.id;
            existingByName[name.toLowerCase()] = created;
        }

        queryClient.invalidateQueries({queryKey: ['personCustomFields']});
    }

    const {personColumns, addressColumns, phoneField, idField} = personImportFieldGroups;
    const idHeader = mappedColumns[idField];

    // Lookup for validating existing custom field values (dropdown/multiselect option matching).
    const customFieldById = (customFields || []).reduce((acc, cf) => { acc[cf.id] = cf; return acc; }, {});

    // 2) Resolve existing people via ImportAssociations (external CSV id -> Reach person id).
    // Paginate because Supabase caps queries at 1000 rows by default.
    let associationByValue = {};
    if(idHeader){
        const pageSize = 1000;
        let allAssociations = [];
        let offset = 0;
        let assocError = null;

        while(true){
            const {data, error} = await supabase
                .from("ImportAssociations")
                .select("value, associatedID")
                .eq("organizationID", orgID)
                .eq("associatedTable", "people")
                .range(offset, offset + pageSize - 1);

            if(error){ assocError = error; break; }
            allAssociations.push(...(data || []));
            if(!data || data.length < pageSize) break;
            offset += pageSize;
        }

        if(assocError){
            ErrorHandler(Error(assocError.message), {mutation: "importPeople", input: {error: assocError}}, `Something went wrong checking for existing records`)
            throw Error(assocError.message);
        }

        // Normalize keys: trim + stringify so numeric breeze IDs match string CSV values.
        associationByValue = allAssociations.reduce((acc, a) => { acc[String(a.value).trim()] = a.associatedID; return acc; }, {});
    }

    // Build the destination pieces (People columns, Address, phone, custom fields) for one row.
    const buildRow = (row) => {
        const person = {};
        const address = {};
        const customFieldValues = {};
        let phoneNumber = null;

        for(const [fieldName, csvHeader] of Object.entries(mappedColumns)){
            if(!csvHeader) continue;
            const raw = row[csvHeader];
            const value = typeof raw === 'string' ? raw.trim() : raw;

            if(fieldName === idField) continue;
            if(fieldName === phoneField){ phoneNumber = value || null; continue; }
            if(addressColumns.includes(fieldName)){ if(value) address[fieldName] = value; continue; }
            if(personColumns.includes(fieldName)){
                if(fieldName === 'isArchived') person[fieldName] = parseImportBoolean(value);
                else if(fieldName === 'dateOfBirth') person[fieldName] = normalizeImportDate(value);
                else if(fieldName === 'familyRole') person[fieldName] = normalizeImportEnum(value, VALID_FAMILY_ROLES);
                else person[fieldName] = (value === '' || value == null) ? null : value;
                continue;
            }
            // Anything else is an existing custom field (fieldName === custom field id).
            // Free orgs never read/write custom field values.
            if(customFieldsMode !== 'skip' && value !== '' && value != null){
                const cf = customFieldById[fieldName];
                if(cf?.type === 'Dropdown'){
                    if(cf.options?.includes(value)) customFieldValues[fieldName] = value;
                    // else: ignore — value doesn't match available options
                } else if(cf?.type === 'Multiselect'){
                    const parts = String(value).split(',').map((s) => s.trim()).filter(Boolean);
                    const valid = parts.filter((p) => cf.options?.includes(p));
                    if(valid.length > 0) customFieldValues[fieldName] = valid.length === 1 ? valid[0] : valid;
                    // else: ignore — no values match available options
                } else {
                    customFieldValues[fieldName] = value;
                }
            }
        }

        // New custom fields are keyed by their source CSV header, not by mappedColumns.
        for(const [csvHeader, fieldId] of Object.entries(customFieldsMode === 'skip' ? {} : newFieldIdByHeader)){
            const raw = row[csvHeader];
            const value = typeof raw === 'string' ? raw.trim() : raw;
            if(value !== '' && value != null) customFieldValues[fieldId] = value;
        }

        const externalId = idHeader ? String(row[idHeader]).trim() : null;

        return {person, address, customFieldValues, phoneNumber, externalId};
    };

    // 3) Split rows into creates and updates.
    const rowsToCreate = [];
    const rowsToUpdate = [];
    let skipped = 0;

    for(const row of data){
        const built = buildRow(row);

        if(!built.person.firstName && !built.person.lastName && !built.externalId) continue; // empty row

        const existingID = built.externalId ? associationByValue[built.externalId] : null;

        if(existingID){
            if(existingRecordMode === 'skip'){ skipped++; continue; }
            rowsToUpdate.push({personID: existingID, built});
        } else {
            rowsToCreate.push(built);
        }
    }

    // 4) Creates (batched).
    if(rowsToCreate.length > 0){
        const addressesToInsert = [];
        const peopleToInsert = [];
        const phonesToInsert = [];
        const associationsToInsert = [];
        const customFieldPatches = []; // rpc mode: {personID, patch} applied post-insert

        for(const built of rowsToCreate){
            const personID = Crypto.randomUUID();

            let addressID = null;
            if(Object.keys(built.address).length > 0){
                addressID = Crypto.randomUUID();
                addressesToInsert.push({id: addressID, organizationID: orgID, ...built.address});
            }

            peopleToInsert.push({
                id: personID,
                organizationID: orgID,
                addressID,
                // Legacy mode keeps the pre-migration inline write; rpc/skip
                // modes never put the paid column in the insert payload.
                ...(customFieldsMode === 'legacy' ? { customFieldValues: Object.keys(built.customFieldValues).length > 0 ? built.customFieldValues : null } : {}),
                ...built.person,
            });
            if(customFieldsMode === 'rpc' && Object.keys(built.customFieldValues).length > 0){
                customFieldPatches.push({personID, patch: built.customFieldValues});
            }

            if(built.phoneNumber){
                phonesToInsert.push({personID, organizationID: orgID, phoneNumber: built.phoneNumber, label: 'Mobile', smsOptInStatus: 'never_opted_in', private: false});
            }

            if(built.externalId){
                associationsToInsert.push({value: built.externalId, associatedTable: 'people', associatedID: personID, organizationID: orgID});
            }
        }

        await insertInChunks("Addresses", addressesToInsert);
        await insertInChunks("People", peopleToInsert);
        await insertInChunks("PeoplePhones", phonesToInsert);
        await insertInChunks("ImportAssociations", associationsToInsert);

        for(const {personID, patch} of customFieldPatches){
            const applied = await updatePersonCustomFieldValues(personID, patch);
            if(applied === false){
                ErrorHandler(Error('Custom field import failed'), {mutation: "importPeople", input: {personID}}, `Something went wrong importing custom field values`)
                throw Error('Custom field import failed');
            }
        }
    }

    // 5) Updates (per row, merging custom field values and address).
    if(rowsToUpdate.length > 0){
        const ids = rowsToUpdate.map((r) => r.personID);
        // The paid column is only readable directly on the legacy
        // (pre-migration) schema; rpc mode merges server-side via the RPC.
        const needsLegacyValues = customFieldsMode === 'legacy' && rowsToUpdate.some(r => Object.keys(r.built.customFieldValues).length > 0);
        const {data: existingPeople} = await supabase.from("People")
            .select(needsLegacyValues ? "id, addressID, customFieldValues" : "id, addressID")
            .in("id", ids);
        const existingByID = (existingPeople || []).reduce((acc, p) => { acc[p.id] = p; return acc; }, {});

        for(const {personID, built} of rowsToUpdate){
            const existing = existingByID[personID] || {};
            const update = {...built.person};

            if(customFieldsMode === 'legacy' && Object.keys(built.customFieldValues).length > 0){
                update.customFieldValues = {...(existing.customFieldValues || {}), ...built.customFieldValues};
            }

            if(Object.keys(built.address).length > 0){
                if(existing.addressID){
                    await supabase.from("Addresses").update(built.address).eq("id", existing.addressID);
                } else {
                    const addressID = Crypto.randomUUID();
                    await supabase.from("Addresses").insert({id: addressID, organizationID: orgID, ...built.address});
                    update.addressID = addressID;
                }
            }

            const {error: updateError} = await supabase.from("People").update(update).eq("id", personID);
            if(updateError){
                ErrorHandler(Error(updateError.message), {mutation: "importPeople", input: {personID, update, error: updateError}}, `Something went wrong updating an existing person`)
                throw Error(updateError.message);
            }

            if(customFieldsMode === 'rpc' && Object.keys(built.customFieldValues).length > 0){
                const applied = await updatePersonCustomFieldValues(personID, built.customFieldValues);
                if(applied === false){
                    ErrorHandler(Error('Custom field import failed'), {mutation: "importPeople", input: {personID}}, `Something went wrong importing custom field values`)
                    throw Error('Custom field import failed');
                }
            }

            if(built.phoneNumber){
                const {data: existingPhones} = await supabase.from("PeoplePhones").select("phoneNumber").eq("personID", personID).eq("phoneNumber", built.phoneNumber);
                if(!existingPhones || existingPhones.length === 0){
                    await supabase.from("PeoplePhones").insert({personID, organizationID: orgID, phoneNumber: built.phoneNumber, label: 'Mobile', smsOptInStatus: 'never_opted_in', private: false});
                }
            }
        }
    }

    queryClient.invalidateQueries({queryKey: ['importAssociations']});
    queryClient.invalidateQueries({queryKey: ['people']});

    if (rowsToCreate.length + rowsToUpdate.length > 0) {
        await recordPeopleImportSetup(orgID);
        queryClient.invalidateQueries({ queryKey: ['organizationSetup', orgID] });
    }
    return {created: rowsToCreate.length, updated: rowsToUpdate.length, skipped};
}

export { 
    getLoggedInUserInfo,
    getPersonProfile,
    getFreePersonProfile,
    getPersonBasic,
    getPersonsDynamicRoles,
    updateAvatarUpdatedAt,
    mergePeople,
    ignorePotentialDuplicate,
    getPotentialDuplicates,
    findPeopleByName,
    getPersonForMerge,
    getPeople,
    getPeopleWithConversationAccess,
    getPersonCustomFields,
    getPersonCustomField,
    getPersonCustomFieldSections,
    deletePersonCustomField,
    deletePersonCustomFieldSection,
    savePersonCustomField,
    savePersonCustomFieldSection,
    createPersonCustomField,
    updatePersonCustomField,
    createFamily,
    savePerson,
    bulkUpdatePeople,
    saveAddress,
    getAddress,
    getAddresses,
    archivePerson,
    changeAdminStatus,
    getProtectedPersonFields,
    assignPermissions,
    updateBarcodes,
    getPersonCustomFieldSection,
    exportPeopleData,
    getAllPeopleIdsForSelection,
    getAllPeopleForSelection,
    getPeopleByIds,
    getReachUserIdsAmong,
    importPeople,
    getPersonCustomFieldValues,
    updatePersonCustomFieldValues
}